import { inject, Injectable } from '@angular/core';
import { ApplicationDto, ApplicationDefinitionDto, OrganizationVo, ApplicationGroupDto } from './application.do';
import { cloneDeep } from 'lodash-es';
import { map, Observable, ReplaySubject, distinctUntilChanged, take } from 'rxjs';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { selectSelectedOrganization } from '../../management/newversion/state/organizations/organizations.selectors';
import { OrganizationDto } from '../../management/newversion/state/organizations/organizations.model';
import { IAppConfigDesignerConfig, IAppConfigSetting } from '../designer-config-service/appconfig/app-config-designer';

@Injectable({
  providedIn: 'root'
})
export class ApplicationContextService {
  private store = inject(Store);
  private actions$ = inject(Actions);

  private _applicationDefinitionId: number;
  private _applicationDefinition: ApplicationDefinitionDto;
  private _applicationGroupId: number;
  private _applicationGroup: ApplicationGroupDto;
  private _applicationId: number;
  private _application: ApplicationDto;
  private _applicationIdObservable = new ReplaySubject<number>(1);
  private _applicationGroupIdObservable = new ReplaySubject<number>(1);
  private _applicationObservable = new ReplaySubject<ApplicationDto>(1);
  private _appConfig: IAppConfigDesignerConfig;
  private _appConfigObservable = new ReplaySubject<IAppConfigDesignerConfig>(1);

  // Organization state from NgRx store (single source of truth)
  public currentOrganization$: Observable<OrganizationVo> = this.store.select(selectSelectedOrganization).pipe(
    map(orgVo => orgVo ? this.convertToOrganization(orgVo) : null)
  );

  // Method for getting current value (replaces .getValue())
  getCurrentOrganization(): OrganizationVo | null {
    let org: OrganizationVo = null;
    this.currentOrganization$.pipe(take(1)).subscribe(o => org = o);
    return org;
  }

  private convertToOrganization(dto: OrganizationDto): OrganizationVo {
    const org = new OrganizationVo();
    org.id = dto.id;
    org.name = dto.name;
    org.tenantId = dto.tenantId;
    org.homeTenantId = dto.homeTenantId;
    org.externalEntraIdDomainName = dto.externalEntraIdDomainName;
    org.externalEntraId = !!dto.externalEntraIdDomainName?.trim();
    org.devOpsOrganization = dto.devOpsOrganization;
    org.crmOrganization = dto.crmOrganization;
    org.portalUrl = dto.portalUrl;
    return org;
  }

  public applicationIdObservable = this._applicationIdObservable.pipe(distinctUntilChanged());
  public applicationGroupIdObservable = this._applicationGroupIdObservable.pipe(distinctUntilChanged());
  public applicationObservable = this._applicationObservable.pipe(distinctUntilChanged());
  public appConfigObservable = this._appConfigObservable.pipe(distinctUntilChanged());

  get applicationDefinitionId() {
    return this._applicationDefinition ? this._applicationDefinition.id : this._applicationDefinitionId;
  }

  set applicationDefinitionId(value: number) {
    this._applicationDefinitionId = value;
  }

  get applicationDefinition(): ApplicationDefinitionDto {
    return cloneDeep(this._applicationDefinition);
  }
  set applicationDefinition(value: ApplicationDefinitionDto) {
    this._applicationDefinition = value;
  }

  get applicationGroupId() {
    return this._applicationGroup ? this._applicationGroup.id : this._applicationGroupId;
  }

  set applicationGroupId(value: number) {
    this._applicationGroupId = value;
    this._applicationGroupIdObservable.next(value);
  }

  get applicationGroup(): ApplicationGroupDto {
    return cloneDeep(this._applicationGroup);
  }
  set applicationGroup(value: ApplicationGroupDto) {
    this._applicationGroup = value;
    this._applicationGroupIdObservable.next(value?.id);
  }

  get applicationId() {
    return this._application ? this._application.id : this._applicationId;
  }

  set applicationId(value: number) {
    this._applicationId = value;
    this._applicationIdObservable.next(value);
  }

  get application() {
    return cloneDeep(this._application);
  }
  set application(value: ApplicationDto) {
    this.setApplication(value, true);
  }

  setApplication(value: ApplicationDto, notifyChanges: boolean) {
    this._application = value;
    this.applicationId = value?.id;
    if (notifyChanges) {
      this._applicationObservable.next(value);
    }
  }

  get appConfig(): IAppConfigDesignerConfig {
    return cloneDeep(this._appConfig);
  }

  set appConfig(value: IAppConfigDesignerConfig) {
    this._appConfig = value;
    this._appConfigObservable.next(value);
  }


  getSetting(settingName: string, moduleReferenceName: string): IAppConfigSetting {
    if (moduleReferenceName) {
      const reference = this._appConfig.references?.find(r => r.referenceName === moduleReferenceName);
      const mapping = reference?.settingsMappings?.find(m => m.from === settingName);
      if (!mapping) {
        return null;
      }
      const setting = this._appConfig.settings?.find(s => s.name === mapping.to);
      return cloneDeep(setting);
    } else {
      const setting = this._appConfig.settings?.find(s => s.name === settingName);
      return cloneDeep(setting);
    }
  }
}
